import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import useAuth from "../../hooks/useAuth";
import LogoBlanc from "../LogoBlanc";
import Scrollbar from "../Scrollbar";
import NavSection from "../NavSection";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { sectionsAdmin, sections2, sections3, sections4, sectionsDynamic, PATH_PERMISSIONS } from "./sidebarSections";
import usePermissions from "../../hooks/usePermissions";
import { toneAlpha, withAlpha, lightVeil, getTone, grey } from '../../theme/tones';
import { brand } from '../../theme/tokens';

/** L'orange du logotype, seconde couleur de la marque. */
const orangeLight = getTone('orange').light;

/**
 * Largeurs du rail. La barre haute ne dépend plus d'elles (elle court sur
 * toute la largeur), mais le décalage du contenu, oui — d'où l'export.
 */
export const SIDEBAR_WIDTH_OPEN = 264;
export const SIDEBAR_WIDTH_CLOSED = 76;

/** Hauteur de la barre haute : le rail commence juste en dessous. */
export const NAVBAR_HEIGHT = 64;

const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

const listVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.04, delayChildren: 0.05 } }
};

const sectionVariants = {
  hidden: { opacity: 0, x: -12 },
  show: { opacity: 1, x: 0, transition: { type: 'spring', stiffness: 260, damping: 26 } }
};

/**
 * Le rail porte le bleu du logotype — le marine de marque `brand-700`,
 * `#1b2445` — dans les deux modes : c'est la couleur d'identité, elle ne
 * dépend pas du thème.
 *
 * Pour qu'il ne soit pas un simple aplat, il est éclairé par les deux couleurs
 * du logo : un halo d'accent bleu descend de l'angle haut (côté barre, pour
 * raccorder les deux), et une lueur orange — la seconde couleur du logotype —
 * remonte discrètement de l'angle bas. Le marine s'assombrit d'un cran vers
 * le bas, ce qui donne au rail sa profondeur.
 */
const surfaceSx = () => ({
  backgroundColor: brand[700],
  backgroundImage: [
    `radial-gradient(420px 320px at 0% 0%, ${toneAlpha('blue', 0.42)} 0%, ${toneAlpha('blue', 0)} 60%)`,
    `radial-gradient(360px 300px at 100% 100%, ${withAlpha(orangeLight, 0.16)} 0%, ${withAlpha(orangeLight, 0)} 60%)`,
    `linear-gradient(180deg, ${brand[700]} 0%, ${brand[800]} 100%)`
  ].join(', '),
  borderRight: `1px solid ${lightVeil(0.08)}`,
  boxShadow: `inset 0 1px 0 ${lightVeil(0.08)}`
});

const DashboardSidebar = ({ onMobileClose, openMobile, drawerOpen, setDrawerOpen }) => {
  const location = useLocation();
  const { user } = useAuth();
  const { ready: permissionsReady, can, isSuperAdmin } = usePermissions();
  const lgUp = useMediaQuery((theme) => theme.breakpoints.up("lg"));
  const expanded = drawerOpen || openMobile;

  // Gestion de l'état du drawer
  const toggleDrawer = () => {
    const newState = !drawerOpen;
    setDrawerOpen(newState);
    localStorage.setItem("drawerOpen", JSON.stringify(newState));
  };

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
  }, [location.pathname]);

  useEffect(() => {
    const storedDrawerOpen = localStorage.getItem("drawerOpen");
    if (storedDrawerOpen !== null) {
      setDrawerOpen(JSON.parse(storedDrawerOpen));
    }
  }, [setDrawerOpen]);


  // Menu piloté par le rôle : le menu complet, réduit à ce qu'il autorise.
  const filterByPermissions = (sections) => sections
    .map((section) => ({
      ...section,
      items: Array.isArray(section.items)
        ? section.items.filter((item) => {
          const perm = PATH_PERMISSIONS[item.path];
          return perm ? can(perm) : isSuperAdmin;
        })
        : section.items,
    }))
    .filter((section) => !Array.isArray(section.items) || section.items.length > 0);

  // Fonction pour déterminer les sections dynamiquement
  const getSections = () => {
    if (permissionsReady) return filterByPermissions(sectionsDynamic);
    // Repli tant que l'API ne renvoie pas les permissions : menus historiques.
    if (user.access_level === 0) return sectionsAdmin;
    if (user.id_service === 3) return sections3;
    if (user.id_service === 4) return sections4;
    return sections2;
  };

  // Le "Suivi des tâches" et le "Suivi inscrits/désinscrits" ne sont visibles que
  // pour les services Relance (id_service 4) et Administrateur (access_level 0).
  const canSeeTaskTracking = user.access_level === 0 || user.id_service === 4;
  const RESTRICTED_TASK_PATHS = ["/dashboard/tasks-tracking", "/dashboard/learners-tracking"];
  const filterTaskTracking = (sections) => {
    // Avec les permissions actives, ces entrées ont déjà leur propre clé.
    if (permissionsReady || canSeeTaskTracking) return sections;
    return sections.map((section) => ({
      ...section,
      items: Array.isArray(section.items)
        ? section.items.filter((item) => !RESTRICTED_TASK_PATHS.includes(item.path))
        : section.items,
    }));
  };

  const content = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* En mobile le rail recouvre la barre haute : il porte alors la marque
          lui-même, sinon on ne saurait plus où l'on est. */}
      {!lgUp && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: NAVBAR_HEIGHT,
            px: 2.5,
            flexShrink: 0,
            borderBottom: `1px solid ${lightVeil(0.08)}`
          }}
        >
          <LogoBlanc style={{ height: 22, width: 'auto' }} />
        </Box>
      )}

      {/* ----------------------------------------------- Navigation -- */}
      <Box sx={{ flex: '1 1 auto', minHeight: 0, position: 'relative' }}>
        <Scrollbar options={{ suppressScrollX: true }} style={{ height: '100%' }}>
          <Box
            component={motion.div}
            variants={listVariants}
            initial="hidden"
            animate="show"
            sx={{
              px: expanded ? 1.25 : 1,
              py: 1.5,
              transition: `padding 0.3s ${EASE}`
            }}
          >
            {filterTaskTracking(getSections()).map((section) => (
              <Box
                component={motion.div}
                variants={sectionVariants}
                key={section.title}
                sx={{ '& + &': { mt: expanded ? 1.5 : 0.75 } }}
              >
                <NavSection
                  pathname={location.pathname}
                  {...section}
                  drawerOpen={drawerOpen}
                  openMobile={openMobile}
                />
              </Box>
            ))}
          </Box>
        </Scrollbar>
      </Box>

      {/* ---------------------------------------------------- Pied -- */}
      {/* Le repli vit dans le pied du rail, en pleine largeur : plus de
          chevron accroché à l'arête, l'affordance est dans le rail lui-même. */}
      {lgUp && (
        <Box
          sx={{
            flexShrink: 0,
            p: 1,
            borderTop: `1px solid ${lightVeil(0.08)}`
          }}
        >
          {drawerOpen ? (
            <Button
              onClick={toggleDrawer}
              fullWidth
              startIcon={<ChevronLeftIcon sx={{ fontSize: 18 }} />}
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.78rem',
                color: lightVeil(0.6),
                borderRadius: '10px',
                px: 1.25,
                '&:hover': { color: grey(0), backgroundColor: lightVeil(0.08) }
              }}
            >
              Réduire le menu
            </Button>
          ) : (
            <Tooltip title="Déplier le menu" placement="right" arrow>
              <IconButton
                onClick={toggleDrawer}
                sx={{
                  width: '100%',
                  height: 36,
                  borderRadius: '10px',
                  color: lightVeil(0.6),
                  '&:hover': { color: grey(0), backgroundColor: lightVeil(0.08) }
                }}
              >
                <ChevronRightIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );

  if (lgUp) {
    return (
      <Box>
        <Drawer
          anchor="left"
          onClose={toggleDrawer}
          open={drawerOpen}
          PaperProps={{
            sx: {
              ...surfaceSx(),
              // Sous la barre haute, pas à côté : c'est elle qui porte la
              // marque et court sur toute la largeur.
              top: `${NAVBAR_HEIGHT}px !important`,
              height: `calc(100% - ${NAVBAR_HEIGHT}px) !important`,
              width: drawerOpen ? `${SIDEBAR_WIDTH_OPEN}px` : `${SIDEBAR_WIDTH_CLOSED}px`,
              transition: `width 0.3s ${EASE}`,
              overflow: 'hidden',
              border: 'none',
              borderRight: `1px solid ${lightVeil(0.08)}`
            },
          }}
          variant="permanent"
        >
          {content}
        </Drawer>
      </Box>
    );
  }

  return (
    <Drawer
      anchor="left"
      onClose={onMobileClose}
      open={openMobile}
      // Au-dessus de la barre haute : le rail est plein écran en mobile,
      // il ne doit pas passer sous la barre translucide.
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 200 }}
      PaperProps={{
        sx: {
          ...surfaceSx(),
          width: SIDEBAR_WIDTH_OPEN,
          border: 'none',
          boxShadow: `24px 0 48px -24px ${withAlpha(brand[900], 0.6)}`
        },
      }}
      variant="temporary"
    >
      {content}
    </Drawer>
  );
};

DashboardSidebar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
  setDrawerOpen: PropTypes.func.isRequired,
  drawerOpen: PropTypes.bool.isRequired,
};

export default DashboardSidebar;
